import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPinIcon, PhoneIcon, EnvelopeIcon } from '@heroicons/react/24/solid';
import {
  FaYoutube,
  FaInstagram,
  FaFacebookF,
  FaLinkedinIn,
  FaTwitter,
  FaWhatsapp,
  FaTelegramPlane,
  FaPhoneAlt,
} from 'react-icons/fa';
import ContactForm from '../components/ContactForm';
import coachingData from '../data/coaching.json';
import directorsData from '../data/directors.json';

const getInitials = (name) => {
  if (!name) {
    return 'D';
  }

  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');
};

const stripToDigits = (value) => (value || '').replace(/\D/g, '');

const buildPhoneHref = (value) => {
  const digits = stripToDigits(value);

  if (!digits) {
    return '';
  }

  if (digits.length === 10) {
    return `tel:+91${digits}`;
  }

  if (digits.startsWith('91') && digits.length === 12) {
    return `tel:+${digits}`;
  }

  return `tel:${digits}`;
};

const buildWhatsappHref = (value) => {
  const digits = stripToDigits(value);

  if (!digits) {
    return '';
  }

  const payload = digits.length === 10 ? `91${digits}` : digits;
  return `https://wa.me/${payload}`;
};

const buildTelegramHref = (value) => {
  const digits = stripToDigits(value);

  if (!digits) {
    return '';
  }

  const payload = digits.length === 10 ? `91${digits}` : digits;
  return `https://t.me/+${payload}`;
};

const formatPhoneDisplay = (value) => {
  const digits = stripToDigits(value);

  if (!digits) {
    return value || '';
  }

  if (digits.length === 10) {
    return `+91 ${digits}`;
  }

  if (digits.length === 12 && digits.startsWith('91')) {
    return `+${digits}`;
  }

  return value || digits;
};

const buildMailtoHref = (value) => {
  const email = (value || '').trim();
  return email ? `mailto:${email}` : '';
};

const joinDirectorMessages = (messages = []) => messages.filter(Boolean).join('\n\n');

const CARD_LINE_LIMIT = 10;
const AVG_WORDS_PER_LINE = 8;

const parseLineSeparatedString = (rawMessage) => {
  if (!rawMessage) {
    return [];
  }

  return String(rawMessage)
    .split(/\r?\n+/)
    .map((line) => line.trim())
    .filter(Boolean);
};

const extractCoverMessages = (data) => {
  if (!data) {
    return [];
  }

  if (Array.isArray(data.coverMessages)) {
    return data.coverMessages.map((message) => String(message).trim()).filter(Boolean);
  }

  if (Array.isArray(data.cover_message)) {
    return data.cover_message.map((message) => String(message).trim()).filter(Boolean);
  }

  if (typeof data.coverMessages === 'string') {
    return parseLineSeparatedString(data.coverMessages);
  }

  if (typeof data.cover_message === 'string') {
    return parseLineSeparatedString(data.cover_message);
  }

  return [];
};

const buildCollageImages = (data) => {
  if (!data) {
    return [];
  }

  if (Array.isArray(data.collageImages) && data.collageImages.length > 0) {
    return data.collageImages
      .map((item, index) => {
        const src = item?.src || item?.url;
        if (!src) {
          return null;
        }

        return {
          id: item?.id || `collage-${index}`,
          src,
          spanClass: item?.spanClass || item?.span || 'col-span-1 row-span-1',
        };
      })
      .filter(Boolean);
  }

  const images = [];

  if (data?.cover_main_image) {
    images.push({ id: 'main', src: data.cover_main_image, spanClass: 'col-span-2 row-span-2' });
  }

  const sideImages = [
    data?.cover_side_image1,
    data?.cover_side_image2,
    data?.cover_side_image3,
    data?.cover_side_image4,
  ].filter(Boolean);

  sideImages.forEach((src, index) => {
    images.push({ id: `side-${index + 1}`, src, spanClass: 'col-span-1 row-span-1' });
  });

  return images;
};

const buildSocialLinks = (data) => {
  const socials = data?.socials || {};

  return {
    youtube: socials.youtube || socials.youtube_url || data?.youtube_url || data?.youtube,
    instagram: socials.instagram || socials.instagram_url || data?.instagram_url || data?.instagram,
    facebook: socials.facebook || socials.facebook_url || data?.facebook_url || data?.facebook,
    linkedin: socials.linkedin || socials.linkedin_url || data?.linkedin_url || data?.linkedin,
    twitter:
      socials.twitter || socials.twitter_url || socials.x || data?.twitter_url || data?.twitter || data?.x,
  };
};

const extractContactInfo = (data) => {
  const contact = data?.contact || {};

  const phones = Array.isArray(contact.phones)
    ? contact.phones
    : [data?.contact_number_1, data?.contact_number_2];

  const emails = Array.isArray(contact.emails)
    ? contact.emails
    : [data?.email, data?.email_2];

  const address = contact.address || data?.address || '';
  const whatsapp = contact.whatsapp || data?.whatsapp_number || phones?.[0];

  return {
    address,
    phones: phones.filter(Boolean),
    emails: emails.filter(Boolean),
    whatsapp,
  };
};

const LandingPage = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const coachingName = coachingData?.name || 'Savvy Academy';
  const tagline = coachingData?.tagline || coachingName;
  const coverTitle = coachingData?.cover_title || coachingData?.coverTitle || `Welcome to ${coachingName}`;
  const coverMessages = extractCoverMessages(coachingData);
  const collageImages = buildCollageImages(coachingData);
  const socials = buildSocialLinks(coachingData);
  const contactInfo = extractContactInfo(coachingData);

  const collageSource = collageImages.length > 0 ? collageImages : fallbackCollage;
  const primaryCollageImage = collageSource[0];
  const secondaryCollageImages = collageSource.slice(1, 5);
  const normalizedCollage = [];

  if (primaryCollageImage) {
    normalizedCollage.push({
      ...primaryCollageImage,
      spanClass: 'col-span-2 row-span-2',
    });
  }

  secondaryCollageImages.forEach((image, index) => {
    normalizedCollage.push({
      ...image,
      spanClass: 'col-span-1 row-span-1',
      key: `${image.id || image.src || 'collage'}-${index}`,
    });
  });

  const contactPhones = contactInfo.phones;
  const phoneEntries = contactPhones.map((number) => {
    const href = buildPhoneHref(number);
    return {
      href: href || undefined,
      display: formatPhoneDisplay(number),
    };
  }).filter((entry) => entry.display);

  const primaryPhone = contactPhones[0];
  const phoneHref = buildPhoneHref(primaryPhone);
  const whatsappHref = buildWhatsappHref(contactInfo.whatsapp);
  const telegramHref = buildTelegramHref(contactInfo.whatsapp);
  const whatsappDisplay = formatPhoneDisplay(contactInfo.whatsapp);

  const address = contactInfo.address;
  const emails = contactInfo.emails;
  const emailEntries = emails.map((value) => ({
    href: buildMailtoHref(value),
    display: value,
  })).filter((entry) => entry.display);
  const [activeDirector, setActiveDirector] = useState(null);

  // Reorder directors: Prakash Verma, Rajesh Kumar, Deepak Kumar
  const directorOrder = [
    'Prakash Verma',
    'Rajesh Kumar',
    'Deepak Kumar',
  ];
  const directorEntries = useMemo(() => {
    // Sort and map directors as per required order
    const sorted = directorOrder
      .map((name) => directorsData.find((d) => d.name === name))
      .filter(Boolean);
    return sorted.map((director) => {
      const fullMessage = joinDirectorMessages(director.messages);
      const words = fullMessage.split(/\s+/).filter(Boolean);
      const shouldTruncate = words.length > CARD_LINE_LIMIT * AVG_WORDS_PER_LINE;
      return {
        ...director,
        fullMessage,
        shouldTruncate,
      };
    });
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const syncViewport = () => {
      setIsMobile(window.innerWidth < 768);
    };

  syncViewport();
  return () => window.removeEventListener('resize', syncViewport);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleMenuState = (event) => {
      setIsMenuOpen(Boolean(event?.detail?.open));
    };

    window.addEventListener('nav-menu-open', handleMenuState);
    return () => window.removeEventListener('nav-menu-open', handleMenuState);
  }, []);

  useEffect(() => {
    if (isMenuOpen && isContactFormOpen) {
      setIsContactFormOpen(false);
    }
  }, [isMenuOpen, isContactFormOpen]);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="relative min-h-[85vh] md:h-screen pb-12 md:pb-0">
        <div className="absolute inset-0 hero-gradient opacity-90"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
          <div className="flex flex-col justify-center h-full py-20 md:py-0">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div className="animate-slide-up">
                <span className="badge-accent mb-5 inline-flex items-center shadow-glow">
                  {tagline || coachingName || 'Savvy Academy'}
                </span>
                <h1 className="text-[32px] md:text-6xl font-bold text-white mb-6 drop-shadow-lg">
                  {coverTitle || `Welcome to ${coachingName || 'Savvy Academy'}`}
                </h1>
                <div className="text-lg md:text-2xl text-white mb-8 max-w-xl space-y-3">
                  {coverMessages.length > 0
                    ? coverMessages.map((message, index) => (
                        <p
                          key={`${message}-${index}`}
                          className="leading-relaxed font-semibold animate-hero-text"
                          style={{ animationDelay: `${0.1 + index * 0.2}s` }}
                        >
                          {message}
                        </p>
                      ))
                    : (
                        <p className="leading-relaxed font-semibold animate-hero-text" style={{ animationDelay: '0.1s' }}>
                          Learn Smart, Achieve More
                        </p>
                      )}
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <Link to="/signup-mobile" className="btn-hero">Get Started</Link>
                  <Link to="/about" className="btn-hero">Learn More</Link>
                </div>
              </div>
              <div className="hidden md:flex justify-center lg:justify-end lg:pr-12 xl:pr-16 animate-fade-in">
                <div className="relative">
                  <div className="absolute -inset-3 rounded-[28px] bg-primary/20 opacity-80 shadow-[0_32px_80px_rgba(9,132,255,0.25)] pointer-events-none -z-10"></div>
                  <div className="relative grid grid-cols-2 gap-2.5 auto-rows-[90px] lg:auto-rows-[132px] w-[260px] sm:w-[340px] lg:w-[400px] -translate-y-8 -translate-x-6">
                    {normalizedCollage.map((item, index) => (
                      <div key={item.key || item.id || index} className={`group overflow-hidden rounded-[28px] shadow-soft ${item.spanClass}`}>
                        <img
                          src={item.src}
                          alt={`${coachingName || 'Savvy Academy'} collage ${index + 1}`}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Contact Form */}
      {!isMenuOpen && (
        <div className="fixed bottom-6 right-4 md:bottom-10 md:right-10 z-40 animate-slide-up">
          <ContactForm onVisibilityChange={setIsContactFormOpen} />
        </div>
      )}

      {/* Floating Contact Buttons */}
      {!isMenuOpen && (
        <div
          className={`fixed right-4 z-40 flex flex-col gap-3 md:hidden transition-all duration-300 ${
            isMobile
              ? isContactFormOpen
                ? 'top-[28%] -translate-y-1/2'
                : 'top-[38%] -translate-y-1/2'
              : 'top-[8rem] translate-y-0'
          }`}
        >
          <a
            href={phoneHref || undefined}
            onClick={!phoneHref ? (event) => event.preventDefault() : undefined}
            className="flex items-center justify-center w-12 h-12 rounded-full bg-primary-600 text-white shadow-soft transition-transform duration-200 hover:-translate-y-1 hover:bg-primary"
            aria-label={`Call ${coachingName || 'Savvy Academy'}`}
          >
            <FaPhoneAlt size={18} />
          </a>
          <a
            href={whatsappHref || undefined}
            onClick={!whatsappHref ? (event) => event.preventDefault() : undefined}
            target={whatsappHref ? '_blank' : undefined}
            rel={whatsappHref ? 'noopener noreferrer' : undefined}
            className="flex items-center justify-center w-12 h-12 rounded-full bg-success text-white shadow-soft transition-transform duration-200 hover:-translate-y-1 hover:bg-success/90"
            aria-label="Chat on WhatsApp"
          >
            <FaWhatsapp size={20} />
          </a>
          <a
            href={telegramHref || undefined}
            onClick={!telegramHref ? (event) => event.preventDefault() : undefined}
            target={telegramHref ? '_blank' : undefined}
            rel={telegramHref ? 'noopener noreferrer' : undefined}
            className="flex items-center justify-center w-12 h-12 rounded-full bg-[#229ED9] text-white shadow-soft transition-transform duration-200 hover:-translate-y-1 hover:bg-[#1f8bc1]"
            aria-label="Chat on Telegram"
          >
            <FaTelegramPlane size={20} />
          </a>
        </div>
      )}

  {/* Directors Message Section */}
  <section className="py-16 bg-surface">
        <div className="page-container">
          <h2 className="section-heading text-gradient">Message from Our Directors</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {directorEntries.map((director) => {
              const initials = getInitials(director.name);

              return (
                <div
                  key={director.id || director.name}
                  className="card p-6 bg-gradient-to-br from-surface via-surface to-primary/10 h-full flex flex-col items-center"
                >
                  {director.photo ? (
                    <img
                      src={director.photo}
                      alt={director.name || 'Savvy Academy Director'}
                      className="w-24 h-24 rounded-full object-cover ring-4 ring-primary/30 shadow-soft mx-auto mb-3"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-primary/10 text-primary flex items-center justify-center ring-4 ring-primary/20 shadow-soft text-2xl font-semibold mx-auto mb-3">
                      {initials}
                    </div>
                  )}
                  <h3 className="text-xl font-bold text-primary mb-2 text-center">{director.name}</h3>
                  {director.designation && (
                    <span className="inline-block bg-brand-yellow text-black text-xs font-semibold px-3 py-1 rounded-full mb-2">
                      {director.designation}
                    </span>
                  )}
                  {director.subject && (
                    <span className="inline-block bg-brand-yellow text-black text-[13px] font-semibold px-2.5 py-0.5 rounded-full mb-2 whitespace-nowrap">
                      {`${director.subject.charAt(0).toUpperCase()}${director.subject.slice(1).toLowerCase()} faculty`}
                    </span>
                  )}
                  {director.fullMessage && (
                    <div className="text-text-secondary mt-2 leading-relaxed text-sm md:text-base w-full text-left">
                      <p
                        style={{
                          display: '-webkit-box',
                          WebkitLineClamp: CARD_LINE_LIMIT,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {director.fullMessage}
                      </p>
                      {director.shouldTruncate && (
                        <button
                          type="button"
                          onClick={() => setActiveDirector(director)}
                          className="mt-2 inline-flex items-center text-sm md:text-base text-primary hover:text-primary-300 font-semibold"
                        >
                          Read More
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>
      {/* Features Section */}
      <section className="py-16 bg-bg-default">
        <div className="page-container">
          <h2 className="section-heading text-gradient">Why Choose {coachingName || 'Savvy Academy'}?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="card p-6 bg-surface/90 backdrop-blur-sm">
                <div className="flex flex-col items-center text-center space-y-3">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center shadow-soft">
                    <feature.icon className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-primary mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-text-secondary">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-primary-600 text-white">
        <div className="page-container py-12">
          <div className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr_1.1fr] gap-10 md:gap-16 md:items-start md:justify-items-start">
            <div>
              <h3 className="text-xl font-semibold mb-4">Contact Us</h3>
              <div className="space-y-3 text-sm sm:text-base">
                <p className="flex items-start">
                  <MapPinIcon className="w-7 h-7 mr-3 flex-shrink-0 text-white" />
                  <span>{address || `${coachingName || 'Savvy Academy'}`}</span>
                </p>
                <p className="flex items-start">
                  <PhoneIcon className="w-6 h-6 mr-3 mt-0.5 flex-shrink-0 text-white" />
                  <span>
                    {phoneEntries.length > 0
                      ? phoneEntries.map((entry, index) => (
                          <Fragment key={`phone-entry-${index}`}>
                            {index > 0 && ', '}
                            {entry.href ? (
                              <a
                                href={entry.href}
                                className="hover:text-brand-yellow transition-colors"
                              >
                                {entry.display}
                              </a>
                            ) : (
                              entry.display
                            )}
                          </Fragment>
                        ))
                      : '+91 8002516555'}
                  </span>
                </p>
                <p className="flex items-start">
                  <EnvelopeIcon className="w-6 h-6 mr-3 mt-0.5 flex-shrink-0 text-white" />
                  <span>
                    {emailEntries.length > 0
                      ? emailEntries.map((entry, index) => (
                          <Fragment key={`email-entry-${index}`}>
                            {index > 0 && ', '}
                            {entry.href ? (
                              <a
                                href={entry.href}
                                className="hover:text-brand-yellow transition-colors"
                              >
                                {entry.display}
                              </a>
                            ) : (
                              entry.display
                            )}
                          </Fragment>
                        ))
                      : 'savvyacademypatna@gmail.com'}
                  </span>
                </p>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-4">Follow Us</h3>
              <div className="flex flex-wrap gap-2.5">
                <a
                  href={socials.youtube || '#'}
                  className="flex items-center justify-center w-10 h-10 text-white transition-colors duration-200 hover:text-brand-yellow"
                  aria-label={`${coachingName || 'Savvy Academy'} on YouTube`}
                >
                  <FaYoutube size={24} />
                </a>
                <a
                  href={socials.instagram || '#'}
                  className="flex items-center justify-center w-10 h-10 text-white transition-colors duration-200 hover:text-brand-yellow"
                  aria-label={`${coachingName || 'Savvy Academy'} on Instagram`}
                >
                  <FaInstagram size={24} />
                </a>
                <a
                  href={socials.facebook || '#'}
                  className="flex items-center justify-center w-10 h-10 text-white transition-colors duration-200 hover:text-brand-yellow"
                  aria-label={`${coachingName || 'Savvy Academy'} on Facebook`}
                >
                  <FaFacebookF size={22} />
                </a>
                <a
                  href={socials.linkedin || '#'}
                  className="flex items-center justify-center w-10 h-10 text-white transition-colors duration-200 hover:text-brand-yellow"
                  aria-label={`${coachingName || 'Savvy Academy'} on LinkedIn`}
                >
                  <FaLinkedinIn size={22} />
                </a>
                <a
                  href={socials.twitter || '#'}
                  className="flex items-center justify-center w-10 h-10 text-white transition-colors duration-200 hover:text-brand-yellow"
                  aria-label={`${coachingName || 'Savvy Academy'} on Twitter`}
                >
                  <FaTwitter size={22} />
                </a>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-4">Support</h3>
              <p className="mb-4">Connect with us on WhatsApp or Telegram for quick support</p>
              <div className="flex flex-wrap gap-3">
                <a
                  href={whatsappHref || undefined}
                  target={whatsappHref ? '_blank' : undefined}
                  rel={whatsappHref ? 'noopener noreferrer' : undefined}
                  className="inline-flex items-center px-4 py-2 bg-success hover:bg-success/90 text-white rounded-lg transition-transform duration-200 hover:-translate-y-1"
                >
                  <FaWhatsapp size={20} className="mr-2" />
                  {whatsappDisplay ? `WhatsApp ${whatsappDisplay}` : 'WhatsApp'}
                </a>
                <a
                  href={telegramHref || undefined}
                  target={telegramHref ? '_blank' : undefined}
                  rel={telegramHref ? 'noopener noreferrer' : undefined}
                  className="inline-flex items-center px-4 py-2 bg-[#229ED9] hover:bg-[#1f8bc1] text-white rounded-lg transition-transform duration-200 hover:-translate-y-1"
                >
                  <FaTelegramPlane size={20} className="mr-2" />
                  {whatsappDisplay ? `Telegram ${whatsappDisplay}` : 'Telegram'}
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {activeDirector && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="max-w-2xl w-full bg-surface rounded-2xl shadow-glow overflow-hidden">
            <div className="flex items-start justify-between px-6 py-4 border-b border-border">
              <div>
                <h3 className="text-xl font-semibold text-primary">{activeDirector.name}</h3>
              </div>
              <button
                type="button"
                onClick={() => setActiveDirector(null)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary transition-colors duration-200 hover:bg-primary/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
                aria-label="Close director message"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-4 w-4"
                >
                  <path d="M18 6L6 18" />
                  <path d="M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-6 py-5 space-y-4 text-text-secondary leading-relaxed text-sm md:text-base max-h-[60vh] overflow-y-auto">
              {activeDirector.photo && (
                <img
                  src={activeDirector.photo}
                  alt={activeDirector.name}
                  className="w-24 h-24 rounded-full object-cover ring-4 ring-primary/30 shadow-soft"
                />
              )}
              {activeDirector.fullMessage.split(/\n{2,}/).map((paragraph, index) => (
                <p key={`modal-paragraph-${index}`} className="whitespace-pre-line">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ExpertIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
  </svg>
);

const PersonalisedIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5" />
  </svg>
);

const TechnologyIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0V12a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 12V5.25" />
  </svg>
);

const features = [
  {
    title: 'Expert Faculty',
    description: 'Learn from IIT, AIIMS, and NIT alumni with cumulative experience of 10,000+ selections across engineering and medical entrance examinations.',
    icon: ExpertIcon,
  },
  {
    title: 'Personalised Learning',
    description: 'Because everyone is unique, our personalised approach to teaching and learning adapts to your strengths, pace, and goals — guiding you to success your way.',
    icon: PersonalisedIcon,
  },
  {
    title: 'Powered by Technology',
    description: 'State-of-the-art infrastructure, digital-first and AI-enabled learning and assessment that keeps you ahead of the curve.',
    icon: TechnologyIcon,
  }
];

const fallbackCollage = [
  {
    id: 'fallback-main',
    src: '/images/hero-collage/1.png',
  },
  {
    id: 'fallback-side-1',
    src: '/images/hero-collage/2.png',
  },
  {
    id: 'fallback-side-2',
    src: '/images/hero-collage/3.png',
  },
  {
    id: 'fallback-side-3',
    src: '/images/hero-collage/4.png',
  },
  {
    id: 'fallback-side-4',
    src: '/images/hero-collage/5.png',
  },
];

export default LandingPage;